import React, { useEffect } from "react";
import { Form, Row, Col, message } from "antd";
import { useQuery, useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";

// graphql
import GET_COUNTRIES from "graphql/queries/GetCountries";
import GET_BILLING_ADDRESS from "graphql/queries/GetBillingAddress";
import UPDATE_BILLING_ADDRESS from "graphql/mutations/UpdateBillingAddress";

// styling
import {
  Item as FormItem,
  Group as FormGroup,
  GroupLabel as FormGroupLabel,
  Input as FormInput,
  Content as FormContent,
  Container as FormContainer,
  Select as FormSelect,
} from "pages/account/AccountFormStyles";
import { StyledButton } from "pages/account/my_profile/UploadAvatarStyles";

const BillingAddressForm = () => {
  const [form] = Form.useForm();
  const { t } = useTranslation("my_billing");

  // Get countries data
  const { data: countriesData, loading: countriesLoading } = useQuery(
    GET_COUNTRIES,
    {
      fetchPolicy: "no-cache",
    }
  );

  // Get billing info data
  const { data: billingAddressData } = useQuery(GET_BILLING_ADDRESS, {
    fetchPolicy: "no-cache",
  });

  // update billing address
  const [updateBillingAddress] = useMutation(UPDATE_BILLING_ADDRESS, {
    onCompleted: () => {
      message.success(t("Billing address updated successfully!"));
    },
    onError: () => {
      message.error(t("Failed saving changes! Please try again."));
    },
  });

  // effect hook to show billing address if available
  useEffect(() => {
    if (billingAddressData?.me) {
      form.setFieldsValue(billingAddressData.me);
    }
  }, [billingAddressData, form]);

  const onSubmitForm = async (values: any) => {
    await updateBillingAddress({
      variables: {
        input: {
          attributes: values,
        },
      },
    });
  };

  return (
    <FormContainer>
      <Form form={form} layout="vertical" onFinish={onSubmitForm}>
        <FormGroup>
          <FormGroupLabel>{t("BILLING INFO")}</FormGroupLabel>
          <FormContent>
            <Row>
              <Col sm={24} md={13}>
                <FormItem
                  name="billingAddressLine1"
                  label={t("Address Line 1")}
                  rules={[
                    {
                      required: true,
                      message: t("Please input your street address!"),
                    },
                  ]}
                >
                  <FormInput placeholder={t("Street address")} />
                </FormItem>
              </Col>
            </Row>

            <br />
            <Row>
              <Col sm={24} md={13}>
                <FormItem
                  name="billingAddressLine2"
                  label={t("Company Name (optional)")}
                >
                  <FormInput />
                </FormItem>
              </Col>
            </Row>

            <br />
            <Row>
              <Col sm={{ span: 24, offset: 0 }} md={{ span: 6, offset: 0 }}>
                <FormItem
                  name="billingCity"
                  label={t("City")}
                  rules={[
                    { required: true, message: t("Please input your city!") },
                  ]}
                >
                  <FormInput />
                </FormItem>
              </Col>
              <Col sm={{ span: 24, offset: 0 }} md={{ span: 6, offset: 1 }}>
                <FormItem
                  name="billingState"
                  label={t("State")}
                  rules={[
                    { required: true, message: t("Please input your state!") },
                  ]}
                >
                  <FormInput />
                </FormItem>
              </Col>
            </Row>

            <br />
            <Row>
              <Col sm={{ span: 24, offset: 0 }} md={{ span: 6, offset: 0 }}>
                <FormItem
                  name="billingCountry"
                  label={t("Country")}
                  rules={[
                    {
                      required: true,
                      message: t("Please input your country!"),
                    },
                  ]}
                >
                  <FormSelect showSearch loading={countriesLoading}>
                    {countriesData?.placesLivingIn.map(
                      (country: { name: string }) => (
                        <FormSelect.Option
                          value={country.name}
                          key={country.name}
                        >
                          {country.name}
                        </FormSelect.Option>
                      )
                    )}
                  </FormSelect>
                </FormItem>
              </Col>
              <Col sm={{ span: 24, offset: 0 }} md={{ span: 6, offset: 1 }}>
                <FormItem
                  name="billingZipCode"
                  label={t("Postcode")}
                  rules={[
                    {
                      required: true,
                      message: t("Please input your postcode!"),
                    },
                  ]}
                >
                  <FormInput />
                </FormItem>
              </Col>
            </Row>
          </FormContent>
        </FormGroup>
        <StyledButton htmlType="submit">{t("SAVE CHANGES")}</StyledButton>
      </Form>
    </FormContainer>
  );
};

export default BillingAddressForm;
